import { StringDecoder } from 'node:string_decoder';
import { yieldToEventLoop } from '../../../runtime/yield-to-event-loop.js';
import { LINE_CHUNK_SIZE, READ_RANGE_YIELD_EVERY_BYTES } from '../../../files/read/types.js';

/** Count lines the same way as `text.split(/\r?\n/).length`, yielding during long scans. */
export async function countGitBlobLinesAsync(buf: Buffer): Promise<number> {
  if (buf.length === 0) return 1;
  let lines = 1;
  let bytesSinceYield = 0;
  for (let i = 0; i < buf.length; i++) {
    const b = buf[i]!;
    if (b === 0x0a) {
      lines++;
    } else if (b === 0x0d) {
      if (i + 1 < buf.length && buf[i + 1] === 0x0a) {
        lines++;
        i++;
      } else {
        lines++;
      }
    }
    bytesSinceYield++;
    if (bytesSinceYield >= READ_RANGE_YIELD_EVERY_BYTES) {
      await yieldToEventLoop();
      bytesSinceYield = 0;
    }
  }
  return lines;
}

/**
 * Extract a line range from an in-memory git blob, yielding while scanning so the bridge stays responsive.
 */
export async function readGitBlobLineRangeAsync(
  buf: Buffer,
  startLine: number,
  endLine: number,
  lineOffsetIn?: number,
  lineChunkSize: number = LINE_CHUNK_SIZE,
): Promise<{ content: string; totalLines: number; lineOffset?: number }> {
  const bufSize = 64 * 1024;
  const decoder = new StringDecoder('utf8');
  let currentLine = 0;
  const resultLines: string[] = [];
  let partial = '';
  let done = false;
  let skipLine0Chars = typeof lineOffsetIn === 'number' ? lineOffsetIn : 0;
  let line0CharsReturned = 0;
  let line0Accum = '';
  let bytesSinceYield = 0;
  let position = 0;

  const finish = async (): Promise<{ content: string; totalLines: number; lineOffset?: number }> => {
    const totalLines = await countGitBlobLinesAsync(buf);
    return { content: resultLines.join('\n'), totalLines };
  };

  while (!done && position < buf.length) {
    const chunkEnd = Math.min(position + bufSize, buf.length);
    const bytesRead = chunkEnd - position;
    position = chunkEnd;
    bytesSinceYield += bytesRead;
    if (bytesSinceYield >= READ_RANGE_YIELD_EVERY_BYTES) {
      await yieldToEventLoop();
      bytesSinceYield = 0;
    }

    const text = partial + decoder.write(buf.subarray(position - bytesRead, position));
    partial = '';
    let lineStart = 0;
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '\n') {
        const lineContent = (() => {
          let lineEnd = i;
          if (lineEnd > lineStart && text[lineEnd - 1] === '\r') lineEnd--;
          return text.slice(lineStart, lineEnd);
        })();
        if (currentLine === 0 && (startLine === 0 || lineOffsetIn !== undefined)) {
          line0Accum += lineContent;
          const totalLine0 = line0Accum.length;
          if (skipLine0Chars > 0) {
            if (totalLine0 <= skipLine0Chars) {
              skipLine0Chars -= totalLine0;
              line0Accum = '';
              currentLine++;
              lineStart = i + 1;
              if (currentLine > endLine) {
                done = true;
                break;
              }
              continue;
            }
            const from = skipLine0Chars;
            const take = Math.min(lineChunkSize, totalLine0 - from);
            resultLines.push(line0Accum.slice(from, from + take));
            line0CharsReturned += take;
            if (from + take < totalLine0) {
              const totalLines = await countGitBlobLinesAsync(buf);
              return {
                content: resultLines.join('\n'),
                totalLines,
                lineOffset: lineOffsetIn! + line0CharsReturned,
              };
            }
            line0Accum = '';
            skipLine0Chars = 0;
            line0CharsReturned = 0;
          } else if (totalLine0 > lineChunkSize) {
            resultLines.push(line0Accum.slice(0, lineChunkSize));
            const totalLines = await countGitBlobLinesAsync(buf);
            return {
              content: resultLines.join('\n'),
              totalLines,
              lineOffset: lineChunkSize,
            };
          } else {
            resultLines.push(line0Accum);
            line0Accum = '';
          }
        } else if (currentLine >= startLine && currentLine <= endLine) {
          resultLines.push(lineContent);
        }
        currentLine++;
        lineStart = i + 1;
        if (currentLine > endLine) {
          done = true;
          break;
        }
      }
    }
    if (!done) {
      const lineContent = text.slice(lineStart);
      if (currentLine === 0 && (startLine === 0 || lineOffsetIn !== undefined)) {
        line0Accum += lineContent;
        const totalLine0 = line0Accum.length;
        if (skipLine0Chars > 0) {
          if (totalLine0 <= skipLine0Chars) {
            skipLine0Chars -= totalLine0;
            line0Accum = '';
          } else {
            const from = skipLine0Chars;
            const take = Math.min(lineChunkSize, totalLine0 - from);
            resultLines.push(line0Accum.slice(from, from + take));
            const totalLines = await countGitBlobLinesAsync(buf);
            return {
              content: resultLines.join('\n'),
              totalLines,
              lineOffset: (lineOffsetIn ?? 0) + take,
            };
          }
        } else if (totalLine0 > lineChunkSize) {
          resultLines.push(line0Accum.slice(0, lineChunkSize));
          const totalLines = await countGitBlobLinesAsync(buf);
          return {
            content: resultLines.join('\n'),
            totalLines,
            lineOffset: lineChunkSize,
          };
        }
      }
      partial = text.slice(lineStart);
    }
  }

  if (!done) {
    const tail = partial + decoder.end();
    if (currentLine === 0 && (startLine === 0 || lineOffsetIn !== undefined) && tail.length > 0) {
      line0Accum += tail.endsWith('\r') ? tail.slice(0, -1) : tail;
      const totalLine0 = line0Accum.length;
      if (skipLine0Chars > 0) {
        if (totalLine0 <= skipLine0Chars) {
          return finish();
        }
        const from = skipLine0Chars;
        const take = Math.min(lineChunkSize, totalLine0 - from);
        resultLines.push(line0Accum.slice(from, from + take));
        line0CharsReturned += take;
        if (from + take < totalLine0) {
          const totalLines = await countGitBlobLinesAsync(buf);
          return {
            content: resultLines.join('\n'),
            totalLines,
            lineOffset: (lineOffsetIn ?? 0) + line0CharsReturned,
          };
        }
      } else if (totalLine0 > lineChunkSize) {
        resultLines.push(line0Accum.slice(0, lineChunkSize));
        const totalLines = await countGitBlobLinesAsync(buf);
        return {
          content: resultLines.join('\n'),
          totalLines,
          lineOffset: lineChunkSize,
        };
      } else {
        resultLines.push(line0Accum);
      }
    } else if (tail.length > 0 && currentLine >= startLine && currentLine <= endLine) {
      resultLines.push(tail.endsWith('\r') ? tail.slice(0, -1) : tail);
    }
  }

  return finish();
}

/** Decode git blob bytes to UTF-8 text, yielding before and after the synchronous decode. */
export async function decodeGitBlobUtf8Async(buf: Buffer): Promise<string> {
  await yieldToEventLoop();
  const text = buf.toString('utf8');
  await yieldToEventLoop();
  return text;
}

/** Full text read with line count, yielding before/after decode and during line counting. */
export async function readGitBlobUtf8FullAsync(
  buf: Buffer,
): Promise<{ content: string; totalLines: number }> {
  const text = await decodeGitBlobUtf8Async(buf);
  const totalLines = await countGitBlobLinesAsync(buf);
  return { content: text, totalLines };
}
